import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeftIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  useGetCfrBulkSendBatch,
  type CfrBulkSendBatchView,
  type CfrBulkSendCustomerGroupView,
  type CfrBulkSendBatchItemView,
} from "@/lib/api/cfr";
import { RoutePath } from "@/section/router";

/**
 * Batch status page for CFR bulk-send batches. Reached after the
 * finalize step in any of the cfr bulk-send dialogs.
 *
 * Polls the batch until it reaches a terminal status:
 *   - "enqueued" / "running" / "completed" / "completed_with_failures"
 *     / "partially_enqueued" / "failed" / "cancelled"
 *
 * Per spec \u00a77.2 / \u00a77.3 the per-customer-group error message is shown
 * verbatim (no toast collapsing) so admins can act on it.
 */
const TERMINAL_BATCH_STATUSES = new Set([
  "completed",
  "completed_with_failures",
  "failed",
  "cancelled",
]);

const POLL_INTERVAL_MS = 3000;

export function CfrBulkSendBatchPage() {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const isTerminal = (batch: CfrBulkSendBatchView | undefined): boolean =>
    !!batch && TERMINAL_BATCH_STATUSES.has(batch.status);

  const query = useGetCfrBulkSendBatch({
    batch_id: batchId ?? "",
    enabled: !!batchId,
    // Tanstack passes the latest query data. Stop polling once we see
    // a terminal batch status; otherwise keep polling at POLL_INTERVAL_MS.
    refetchInterval: (data) => {
      if (data && data.success) {
        return isTerminal(data.data) ? false : POLL_INTERVAL_MS;
      }
      return POLL_INTERVAL_MS;
    },
  });

  const batch: CfrBulkSendBatchView | undefined = query.data?.data;

  if (!batchId) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Missing batch id</AlertTitle>
        <AlertDescription>No batch id provided in the URL.</AlertDescription>
      </Alert>
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading batch...
      </div>
    );
  }

  if (query.isError || !query.data?.success) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load batch</AlertTitle>
        <AlertDescription>
          {query.error instanceof Error ? query.error.message : "Unknown error"}
        </AlertDescription>
      </Alert>
    );
  }

  if (!batch) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Batch not found</AlertTitle>
        <AlertDescription>Batch id: {batchId}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(RoutePath.CfrDocumentsReports)}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to CFR Reports
        </Button>
        <div className="flex-1" />
        {!isTerminal(batch) && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Polling...
          </div>
        )}
      </div>

      <BatchHeader batch={batch} />

      {batch.customer_groups.length === 0 && batch.ungrouped_items.length === 0 && (
        <div className="text-muted-foreground">No items in this batch.</div>
      )}

      {batch.customer_groups.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Customer Groups ({batch.customer_groups.length})</h2>
          {batch.customer_groups.map((group: CfrBulkSendCustomerGroupView) => (
            <CustomerGroupCard key={group.id} group={group} />
          ))}
        </div>
      )}

      {batch.ungrouped_items.length > 0 && (
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-muted-foreground">
            Ungrouped items ({batch.ungrouped_items.length})
          </h2>
          <p className="text-sm text-muted-foreground">
            These items haven't been grouped yet (pre-finalize state).
          </p>
          <div className="flex flex-col gap-1">
            {batch.ungrouped_items.map((item: CfrBulkSendBatchItemView) => (
              <ItemRow key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BatchHeader({ batch }: { batch: CfrBulkSendBatchView }) {
  const summary = useMemo(() => summarizeGroups(batch.customer_groups), [batch.customer_groups]);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <span>Bulk Send Batch</span>
          <StatusBadge status={batch.status} kind="batch" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Items uploaded" value={`${batch.uploaded_item_count}${batch.expected_item_count ? ` / ${batch.expected_item_count}` : ""}`} />
          <Stat label="Customer groups" value={String(batch.customer_groups.length)} />
          <Stat label="Created" value={formatDateTime(batch.created_at)} />
          <Stat label="Finalized" value={batch.finalized_at ? formatDateTime(batch.finalized_at) : "\u2014"} />
          <Stat label="Sent groups" value={String(summary.sent)} />
          <Stat label="Halted / failed" value={String(summary.failed)} />
          <Stat label="Running" value={String(summary.running)} />
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerGroupCard({ group }: { group: CfrBulkSendCustomerGroupView }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span>{group.customer_long_name ?? group.customer_id}</span>
            <span className="text-sm text-muted-foreground">
              {group.item_count} {group.item_count === 1 ? "document" : "documents"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {group.signing_session_last_status && (
              <StatusBadge status={group.signing_session_last_status} kind="signing" />
            )}
            <StatusBadge status={group.status} kind="group" />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          {group.envelope_document_title && (
            <div className="text-sm">
              <span className="text-muted-foreground">Envelope title: </span>
              <span>{group.envelope_document_title}</span>
            </div>
          )}

          {group.error_message && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="whitespace-pre-wrap break-words">
                {group.error_message}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
            <Stat label="Enqueued at" value={group.enqueued_at ? formatDateTime(group.enqueued_at) : "\u2014"} />
            <Stat label="Sent at" value={group.sent_at ? formatDateTime(group.sent_at) : "\u2014"} />
            <Stat label="Stopped at" value={group.halted_at ? formatDateTime(group.halted_at) : "\u2014"} />
          </div>

          {(group.items.length > 0 && !group.items.some((item: CfrBulkSendBatchItemView) => item.validation_error)) && (
              <div className="mt-2 flex flex-col gap-1 text-sm cursor-pointer select-none text-muted-foreground">
                Items ({group.items.length})
              </div>
          )}
          {(group.items.length > 0 && group.items.some((item: CfrBulkSendBatchItemView) => item.validation_error)) && (
            <details className="text-sm">
              <summary className="cursor-pointer select-none text-muted-foreground">
                Items ({group.items.length})
              </summary>
              <div className="mt-2 flex flex-col gap-1">
            {group.items.map((item: CfrBulkSendBatchItemView) => (
                  <ItemRow key={item.id} item={item} />
            ))}
              </div>
            </details>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ItemRow({ item }: { item: CfrBulkSendBatchItemView }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border px-2 py-1 text-xs">
      <StatusBadge status={item.status} kind="item" />
      {item.validation_error && (
        <span className="text-destructive break-words flex-1">
          {item.validation_error}
        </span>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs uppercase tracking-wide">{label}</div>
      <div className="font-medium break-all">{value}</div>
    </div>
  );
}

function StatusBadge({ status, kind }: { status: string; kind: "batch" | "group" | "item" | "signing" }) {
  const tone = statusTone(status, kind);
  const variant = tone === "ok"
    ? "default"
    : tone === "warn"
    ? "secondary"
    : tone === "error"
    ? "destructive"
    : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}

function statusTone(status: string, kind: "batch" | "group" | "item" | "signing"): "ok" | "warn" | "error" | "neutral" {
  // Error states across all kinds
  if ([
    "failed", "halted", "validation_failed",
    "cancelled", "revoked", "declined", "expired",
  ].includes(status)) {
    return "error";
  }
  // Success / terminal positive
  if ([
    "completed", "sent",
  ].includes(status)) {
    return "ok";
  }
  // In-progress / mid-flight
  if ([
    "staging", "finalizing", "validating", "enqueued",
    "running", "uploading", "pending_validation", "ready_to_enqueue",
    "instance_created", "validated", "partially_enqueued",
    "completed_with_failures",
  ].includes(status)) {
    return "warn";
  }
  return "neutral";
}

function summarizeGroups(groups: CfrBulkSendCustomerGroupView[]): {
  sent: number;
  failed: number;
  running: number;
} {
  let sent = 0;
  let failed = 0;
  let running = 0;
  for (const g of groups) {
    if (g.status === "sent") {
      sent += 1;
    } else if (["halted", "validation_failed", "failed"].includes(g.status)) {
      failed += 1;
    } else if (["enqueued", "running", "ready_to_enqueue", "pending_validation"].includes(g.status)) {
      running += 1;
    }
  }
  return { sent, failed, running };
}

function shortId(id: string | null | undefined): string {
  if (!id) { return "--"; }
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) { return iso; }
  return d.toLocaleString();
}
